import { zodResolver } from "@hookform/resolvers/zod"
import { useEffect } from "react"
import { type Control, Controller, useForm } from "react-hook-form"
import { useErrorDialog } from "@/shared/components/ErrorDialog"
import { Button } from "@/shared/components/ui/button"
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/shared/components/ui/dialog"
import {
  Form,
  FormControl,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from "@/shared/components/ui/form"
import { ApiError } from "@/shared/utils/apiError"
import { formatLocalIsoDate, toDatetimeLocalValue } from "@/shared/utils/time"
import {
  type SleepConflict,
  type SleepSession,
  useCreateSessionMutation,
  useDeleteSessionMutation,
  useUpdateSessionMutation,
} from "./api/sleep.mutations"
import { DateTimePicker } from "./DateTimePicker"
import { SleepTypeSelector } from "./SleepTypeSelector"
import { defaultTypeForHour } from "./sleep.config"
import { type SessionFormInput, type SessionFormValues, sessionFormSchema } from "./sleep.schemas"

// The subset of a session the form edits — callers with view-specific
// session DTOs (e.g. the dashboard) satisfy this without adapting.
export type EditableSession = Pick<SleepSession, "id" | "type" | "startTime" | "endTime">

interface SessionFormDialogProps {
  // Create mode needs childId; edit mode passes the existing session.
  childId?: string
  session?: EditableSession
  onSaved?: () => void
  open: boolean
  onOpenChange: (open: boolean) => void
}

const conflictTime = new Intl.DateTimeFormat("sv-SE", {
  day: "numeric",
  month: "long",
  hour: "2-digit",
  minute: "2-digit",
})

function conflictMessage(conflict: SleepConflict): string {
  const start = conflictTime.format(new Date(conflict.conflictStart))
  const end = conflict.conflictEnd
    ? conflictTime.format(new Date(conflict.conflictEnd))
    : "pågående"
  return `Tiden krockar med ett befintligt pass (${start} – ${end}).`
}

function defaultValues(session?: EditableSession): SessionFormInput {
  if (session) {
    return {
      type: session.type,
      start: toDatetimeLocalValue(new Date(session.startTime)),
      end: session.endTime ? toDatetimeLocalValue(new Date(session.endTime)) : "",
    }
  }
  // Retroactive add: both dates default to today, end defaults to now, start
  // time left unassigned (date-only value → empty time in the picker).
  const now = new Date()
  return {
    type: defaultTypeForHour(now.getHours()),
    start: `${formatLocalIsoDate(now)}T`,
    end: toDatetimeLocalValue(now),
  }
}

export function SessionFormDialog({
  childId,
  session,
  onSaved,
  open,
  onOpenChange,
}: SessionFormDialogProps) {
  const { showApiError } = useErrorDialog()
  const create = useCreateSessionMutation()
  const update = useUpdateSessionMutation()
  const remove = useDeleteSessionMutation()

  const isEdit = session !== undefined

  const form = useForm<SessionFormInput, unknown, SessionFormValues>({
    resolver: zodResolver(sessionFormSchema),
    defaultValues: defaultValues(session),
  })
  // The schema transform renames fields (start→startTime), diverging the control's
  // transformed type from the field-values type; narrow it back for the inputs.
  const fieldControl = form.control as unknown as Control<SessionFormInput>

  // Reset each time the dialog opens; re-reads "now" for create mode.
  useEffect(() => {
    if (open) form.reset(defaultValues(session))
  }, [open, session, form])

  const handleError = (err: unknown) => {
    if (err instanceof ApiError && err.status === 409 && err.body) {
      showApiError(new ApiError(409, conflictMessage(err.body as SleepConflict)), {
        title: "Tiderna överlappar",
      })
      return
    }
    showApiError(err, { title: "Kunde inte spara passet" })
  }

  const onSubmit = (values: SessionFormValues) => {
    const done = () => {
      onOpenChange(false)
      onSaved?.()
    }
    if (isEdit) {
      update.mutate({ id: session.id, values }, { onSuccess: done, onError: handleError })
    } else if (childId) {
      create.mutate({ childId, values }, { onSuccess: done, onError: handleError })
    }
  }

  const onDelete = () => {
    if (!session) return
    remove.mutate(session.id, {
      onSuccess: () => {
        onOpenChange(false)
        onSaved?.()
      },
      onError: (err) => showApiError(err, { title: "Kunde inte ta bort passet" }),
    })
  }

  const isPending = create.isPending || update.isPending

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent>
        <DialogHeader>
          <DialogTitle>{isEdit ? "Redigera sömn" : "Lägg till i efterhand"}</DialogTitle>
        </DialogHeader>
        <Form {...form}>
          <form onSubmit={form.handleSubmit(onSubmit)} className="flex flex-col gap-4">
            <Controller
              control={fieldControl}
              name="type"
              render={({ field }) => (
                <SleepTypeSelector value={field.value} onChange={field.onChange} />
              )}
            />
            <FormField
              control={fieldControl}
              name="start"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>Start</FormLabel>
                  <FormControl>
                    <DateTimePicker value={field.value} onChange={field.onChange} />
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />
            <FormField
              control={fieldControl}
              name="end"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>Slut</FormLabel>
                  <FormControl>
                    <DateTimePicker
                      value={field.value}
                      onChange={field.onChange}
                      clearable
                      clearLabel="Pågående"
                    />
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />
            <div className="flex gap-2">
              {isEdit && (
                <Button
                  type="button"
                  variant="destructive"
                  onClick={onDelete}
                  isLoading={remove.isPending}
                >
                  Ta bort
                </Button>
              )}
              <Button type="submit" className="flex-1" isLoading={isPending}>
                Spara
              </Button>
            </div>
          </form>
        </Form>
      </DialogContent>
    </Dialog>
  )
}
